import {useState, useEffect, useCallback} from 'react';
import {PortalTimelineData} from 'presentation/components/Timeline/types';
import {formatDateString} from 'presentation/utils';
import {DateFormatEnum} from 'presentation/constants';
import {UseAuthOdontoStatusDrawerDataParams} from './types';
import {getAuthOdontoStatusTrack} from './services/getAuthOdontoStatusTrack';
import {formatOdontoStatusTitle} from '../../utils';

export function useAuthOdontoStatusDrawerData({
	authorizationCode,
	procedureCode,
	procedureKeyCode,
	shouldSkipFetch = false,
}: UseAuthOdontoStatusDrawerDataParams) {
	const [timelineData, setTimelineData] = useState<PortalTimelineData[]>([]);
	const [lastUpdate, setLastUpdate] = useState<string>('');
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [hasRetried, setHasRetried] = useState(false);

	const fetchTrackData = useCallback(async () => {
		try {
			setIsLoading(true);
			setIsError(false);
			setHasRetried(true);

			const formattedData = await getAuthOdontoStatusTrack({
				authorizationCode,
				procedureCode,
				procedureKeyCode,
			});

			const formatDescription = (description: string) =>
				formatDateString(
					description,
					DateFormatEnum.DATE_FORMAT_2,
					DateFormatEnum.DATE_FORMAT_8,
				);

			setTimelineData(
				formattedData?.map(item => ({
					title: formatOdontoStatusTitle(item.title),
					description: formatDescription(item.description),
				})),
			);

			if (formattedData?.length > 0) {
				setLastUpdate(formatDescription(formattedData[0]?.description));
			}
		} catch (error) {
			setIsError(true);
			setTimelineData([]);
		} finally {
			setIsLoading(false);
		}
	}, [authorizationCode, procedureCode, procedureKeyCode]);

	useEffect(() => {
		if (shouldSkipFetch && !hasRetried) {
			setIsError(true);
			setTimelineData([]);
			setIsLoading(false);
			return;
		}

		if (!shouldSkipFetch || hasRetried) {
			if (Number.isFinite(authorizationCode) && Number.isFinite(procedureCode)) {
				fetchTrackData();
			} else {
				setIsError(true);
				setTimelineData([]);
				setIsLoading(false);
			}
		}
	}, [authorizationCode, procedureCode, shouldSkipFetch, hasRetried, fetchTrackData]);

	const shouldShowContent = !isLoading && !isError && timelineData.length > 0;

	return {
		timelineData,
		lastUpdate,
		isLoading,
		isError,
		refetch: fetchTrackData,
		shouldShowContent,
	};
}
